import { useCallback, useMemo } from 'react'
import L2Surface from '../l2/L2Surface'
import L2ControlGrid from '../l2/L2ControlGrid'
import L2ControlGridLayout from '../l2/L2ControlGridLayout'
import { PrescriptionHistoryItem } from '@fusion-advantage/formlib'
import styled from '@emotion/styled'

type L3HistoricalMedicationsTableProps = {
	models: PrescriptionHistoryItem[],
	columnCount?: number,
	minColumnCapacity?: number,
}

const L2Column = styled.div(({theme}) => ({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'flex-start',
	height: '100%',
	alignItems: 'flex-start',
	gap: theme.spacing(1),
	background: theme.colors.second
}))

const L2TruncatedTextWrapper = styled.div(({theme}) => ({
	width: '100%'
}))

const L2TruncatedTextCell = styled.p(({theme}) => ({
	whiteSpace: 'nowrap',
	width: '100%',
	textOverflow: 'ellipsis',
	overflow: 'hidden',
	margin: 0,
	padding: 0,
}))

const L3HistoricalMedicationsTable = ({models, minColumnCapacity = 5, columnCount = 4}: L3HistoricalMedicationsTableProps) => {
	const distributeModelsV3 = useCallback((models: PrescriptionHistoryItem[], minColumnCapacity: number, columnCount: number) => {
		let arrays: PrescriptionHistoryItem[][] = []
		
		if(models.length < minColumnCapacity) {
			arrays = Array.from({length: columnCount}, () => [])
			arrays[0] = models

			return arrays
		}

		if(models.length <= minColumnCapacity * columnCount) {
			arrays = Array.from({length: columnCount}, () => [])

			let currArr = 0;

			for(let i = 0; i < models.length; i++) {
				if(arrays[currArr].length < minColumnCapacity) {
					arrays[currArr].push(models[i])
				}
				else {
					++currArr
					arrays[currArr].push(models[i])
				}
			}

			return arrays
		}

		/*
		const distributionPerColumn = Math.floor(models.length / columnCount)
		const maxEvenlyDistributed = distributionPerColumn * columnCount
		const remainingModels = models.length - maxEvenlyDistributed

		for(let i = 0; i < maxEvenlyDistributed; i += distributionPerColumn) {
			if(arrays.length >= columnCount) {
				const lastIdx = arrays.length - 1
				arrays[lastIdx] = arrays[lastIdx].concat(models.slice(i, i + distributionPerColumn))
			}
			else {
				arrays.push(models.slice(i, i + distributionPerColumn))
			}
		}
		*/

		/*
		let currIdx = 0;
		for(let i = maxEvenlyDistributed; i < models.length; i++) {
			arrays[currIdx].push(models[i])
			currIdx++;
		}
		*/

		const distributionPerColumn = Math.floor(models.length / columnCount)
		const maxEvenlyDistributed = distributionPerColumn * columnCount
		const remainingModels = models.length - maxEvenlyDistributed

		for(let i = 0; i < models.length; i += distributionPerColumn) {
			if(arrays.length >= columnCount) {
				const lastIdx = arrays.length - 1
				arrays[lastIdx] = arrays[lastIdx].concat(models.slice(i, i + distributionPerColumn))
			}
			else {
				arrays.push(models.slice(i, i + distributionPerColumn))
			}
		}

		if(remainingModels > 0) {
			const shiftArray = (idx: number, shiftTimes: number) => {
				for(let j = 0; j < shiftTimes; j++) {
					arrays[idx].push(arrays[idx + 1].shift()!)
				}
			} 
	
			
			for(let i = 1; i <= columnCount - 1; i++) {
				shiftArray(i - 1, i)
			}
		}

		return arrays;
	}, [models, minColumnCapacity, columnCount])

	const modelDistribution = useMemo(() => {
		return distributeModelsV3(models, minColumnCapacity, columnCount);
	}, [models, columnCount, minColumnCapacity]) 

	return (
		<L2Surface>
			<L2ControlGrid layout={L2ControlGridLayout.EqualDividedRow} columnCount={columnCount}>
				{
					modelDistribution.map((arr, idx) => (
						<L2Column key={idx}>
							{
								arr.map(model => (
									<L2TruncatedTextWrapper>
										<L2TruncatedTextCell key={model.Product.ID}>{model.Product.Name}</L2TruncatedTextCell>
									</L2TruncatedTextWrapper>
								))
							}
						</L2Column>
					))
				}
			</L2ControlGrid>
		</L2Surface>
	)
}

export default L3HistoricalMedicationsTable